/**
 * Email / deep-link promo for Stripe (and pending checkout).
 * Example: `?checkout=stripe&show_pricing=1&promo=SUMMER25`
 *
 * Prefer `promo` (customer-facing promotion code). `coupon` may be a Stripe
 * coupon id (e.g. musicfy_welcome_50) for admin/ops links — casing preserved.
 */

export const EXPIRED_CHECKOUT_PROMO_MESSAGE =
  "This promo code has expired. You can still complete checkout at the current price.";

export const getPricingCloseDestination = (
  pathname: string,
  asPath: string
): string => {
  if (pathname === "/email-offer") return "/";
  return asPath.split("?")[0] || "/";
};

export const CHECKOUT_PROMO_STORAGE_KEY = "mf_checkout_promo_code";

const isValidPromoOrCoupon = (value: unknown): value is string =>
  typeof value === "string" && /^[A-Za-z0-9_-]{2,64}$/.test(value.trim());

export const normalizeCheckoutPromo = (value: unknown): string | null => {
  if (!isValidPromoOrCoupon(value)) return null;
  return value.trim();
};

/**
 * Prefer `promo` (customer code). Fall back to `coupon` (coupon id or code).
 */
export const parseCheckoutPromoFromQuery = (query: {
  promo?: unknown;
  coupon?: unknown;
}): string | null => {
  return (
    normalizeCheckoutPromo(query.promo) ??
    normalizeCheckoutPromo(query.coupon) ??
    null
  );
};

export const getCheckoutPromoCode = (): string | null => {
  if (typeof window === "undefined") return null;
  try {
    return normalizeCheckoutPromo(
      window.sessionStorage.getItem(CHECKOUT_PROMO_STORAGE_KEY)
    );
  } catch {
    return null;
  }
};

export const setCheckoutPromoCode = (code: string): void => {
  if (typeof window === "undefined") return;
  const normalized = normalizeCheckoutPromo(code);
  if (!normalized) return;
  try {
    window.sessionStorage.setItem(CHECKOUT_PROMO_STORAGE_KEY, normalized);
  } catch {
    // ignore quota / private mode
  }
};

export const clearCheckoutPromoCode = (): void => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(CHECKOUT_PROMO_STORAGE_KEY);
  } catch {
    // ignore
  }
};

/** Persist from URL when present so plan CTAs after Pricing open still apply it. */
export const captureCheckoutPromoFromQuery = (query: {
  promo?: unknown;
  coupon?: unknown;
}): string | null => {
  const code = parseCheckoutPromoFromQuery(query);
  if (code) {
    setCheckoutPromoCode(code);
  }
  return code;
};
